// src/plugins/SessionTrackingPlugin.ts
import { IPipelinePlugin } from '../types/plugin';
import { QueuedEvent } from '../types';
import { PluginContext } from '../types/plugin_context';
import { SessionTrackingPluginConfig, SessionData } from '../types/plugins/sessionTracking';
import { PluginCategory, PluginType } from '../types/plugin_categories';
import {} from /* ILogger */ '../utils/logger';

const DEFAULT_SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const DEFAULT_SESSION_STORAGE_KEY_PREFIX = 'insightsSessionData';

export class SessionTrackingPlugin implements IPipelinePlugin {
  public readonly name = 'SessionTrackingPlugin';
  public readonly pluginType = PluginType.PIPELINE;
  public readonly category = PluginCategory.ENRICHMENT;
  public readonly version = '1.0.0';
  public isActive = false;

  private currentSessionData?: SessionData;
  private sessionStorageKey!: string;
  private sessionTimeoutMs!: number;
  private resolvedConfig?: SessionTrackingPluginConfig;
  private sdkConfig?: PluginContext['config'];
  private context?: PluginContext;

  constructor(config?: SessionTrackingPluginConfig) {
    // Store constructor config as fallback, but prefer context.pluginConfig
    this.resolvedConfig = config;
  }

  public initialize(context: PluginContext): void {
    this.context = context;

    if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') {
      this.context.logger.warn('sessionStorage is not available. Plugin will not operate');
      return;
    }

    this.resolvedConfig = {
      ...this.resolvedConfig,
      ...(context.pluginConfig as SessionTrackingPluginConfig),
    };

    this.sdkConfig = context.config;
    const { clientKey } = this.sdkConfig;
    if (!clientKey) {
      this.context?.logger.error(
        'clientKey is missing from SDK config. Cannot determine sessionStorage key. Plugin will not operate effectively.'
      );
      return;
    }

    this.sessionStorageKey =
      this.resolvedConfig?.sessionStorageKeyName ??
      `${DEFAULT_SESSION_STORAGE_KEY_PREFIX}.${clientKey}`;
    this.sessionTimeoutMs = this.resolvedConfig?.sessionTimeoutMs ?? DEFAULT_SESSION_TIMEOUT_MS;

    this._loadOrRefreshSession();
    this.context?.logger.debug(`Initialized. Session ID: ${this.currentSessionData?.sessionId}`);
    this.isActive = true; // Mark plugin as active after initialization
  }

  private _loadOrRefreshSession(): void {
    if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') return;

    const now = Date.now();
    try {
      const storedDataString = sessionStorage.getItem(this.sessionStorageKey);
      if (storedDataString) {
        const storedData: SessionData = JSON.parse(storedDataString);
        if (
          storedData.sessionId &&
          now - storedData.lastActivityTimestamp < this.sessionTimeoutMs
        ) {
          this.currentSessionData = {
            ...storedData,
            lastActivityTimestamp: now, // Refresh activity timestamp
          };
          this.context?.logger.debug(`Session continued. ID: ${this.currentSessionData.sessionId}`);
          this._saveSessionData();
          return;
        }
        this.context?.logger.debug('Session expired or invalid. Starting a new one.');
      }
    } catch (error) {
      this.context?.logger.error('Error loading session data from sessionStorage:', error);
    }
    this._startNewSession(now);
  }

  private _startNewSession(timestamp: number = Date.now()): void {
    if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') return;

    this.currentSessionData = {
      sessionId: timestamp, // Use timestamp as session ID
      lastActivityTimestamp: timestamp,
    };
    this.context?.logger.debug(`New session started. ID: ${this.currentSessionData.sessionId}`);
    this._saveSessionData();
  }

  private _saveSessionData(): void {
    if (
      typeof window === 'undefined' ||
      typeof window.sessionStorage === 'undefined' ||
      !this.currentSessionData
    )
      return;

    try {
      sessionStorage.setItem(this.sessionStorageKey, JSON.stringify(this.currentSessionData));
    } catch (error) {
      this.context?.logger.error('Error saving session data to sessionStorage:', error);
    }
  }

  public processEvent(event: QueuedEvent): QueuedEvent | null {
    if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') {
      this.context?.logger.warn(
        'sessionStorage not available. Skipping session processing for event "${event.eventType}".'
      );
      return event; // Return event unmodified
    }
    if (!event) {
      // Basic check for null/undefined event
      this.context?.logger.warn('Received null or undefined event object.');
      return null;
    }

    this._ensureSessionIsValid(); // Check and refresh/restart session if needed

    const updatedEvent: QueuedEvent = {
      ...event, // Spread the original event to keep all properties
    };

    if (this.currentSessionData?.sessionId) {
      updatedEvent.sessionId = this.currentSessionData.sessionId;
      // Update last activity timestamp for the current session as an event is being processed
      this.currentSessionData.lastActivityTimestamp = Date.now();
      this._saveSessionData();
    } else {
      this.context?.logger.warn(
        'Session ID not available for event "${event.eventType}". Session properties will not be added.'
      );
    }

    return updatedEvent;
  }

  private _ensureSessionIsValid(): void {
    if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') return;

    const now = Date.now();
    if (
      !this.currentSessionData ||
      now - this.currentSessionData.lastActivityTimestamp >= this.sessionTimeoutMs
    ) {
      this.context?.logger.debug(
        'Session timed out or not found during event processing. Starting a new session.'
      );
      this._startNewSession(now);
    }
    // If session is valid, its lastActivityTimestamp will be updated by processEvent after this call.
  }

  public start(): void {
    this.isActive = true;
    this.context?.logger.debug('Started - now active for session tracking.');
  }

  public stop(): void {
    this.isActive = false;
    this.context?.logger.debug('Stopped - session tracking paused.');
  }

  public destroy(): void {
    if (typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined') {
      if (this.sessionStorageKey) {
        try {
          sessionStorage.removeItem(this.sessionStorageKey);
          this.context?.logger.debug(
            'Session data for key "${this.sessionStorageKey}" removed from sessionStorage.'
          );
        } catch (error) {
          this.context?.logger.error('Error removing session data from sessionStorage:', error);
        }
      } else {
        // This case might occur if initialize() was not called or did not complete.
        this.context?.logger.warn(
          'sessionStorageKey is not set. Cannot remove session data from sessionStorage.'
        );
      }
    } else {
      // sessionStorage not available, nothing to remove from it.
      this.context?.logger.warn('sessionStorage is not available. Cannot remove session data.');
    }

    this.currentSessionData = undefined; // Clear in-memory reference
  }
}
